import { createContext, useContext, useMemo, useState, type Dispatch, type FC, type ReactNode, type SetStateAction } from 'react';

interface AssistantEnabledState {
	enabled: boolean;
	/**
	 * Whether Clerk has a signed-in session. Supplied by `_app`, which knows whether the current route
	 * is even wrapped in a `ClerkProvider` — `useAuth` throws without one, and this provider also
	 * renders on the public customer pages, which have no provider by design.
	 */
	hasSession: boolean;
	/**
	 * Whether Clerk has finished loading, so `hasSession` is a real answer rather than "not yet known".
	 * Both states report `hasSession: false`, and consumers that cache their first read need to tell
	 * them apart — see the thread-list adapter in GlazierAssistantProvider.
	 *
	 * Clerk-free routes are resolved from the moment they mount: they will never have a session.
	 */
	sessionResolved: boolean;
	setEnabled: Dispatch<SetStateAction<boolean>>;
}

const AssistantEnabledContext = createContext<AssistantEnabledState>({
	enabled: false,
	hasSession: false,
	sessionResolved: true,
	setEnabled: () => {},
});

export const useAssistantEnabled = () => useContext(AssistantEnabledContext);

/**
 * Allows the GlazierAssistantProvider to live at _app level while initialization is
 * controlled within a UserProvider, where we can tell if the business has this feature
 * enabled.
 * It also prevents issues loading the assistant in mobile WebViews.
 */
export const AssistantEnabledProvider: FC<{ children: ReactNode; hasSession?: boolean; sessionResolved?: boolean }> = ({
	children,
	hasSession = false,
	sessionResolved = true,
}) => {
	const [enabled, setEnabled] = useState(false);

	const value = useMemo(() => ({ enabled, hasSession, sessionResolved, setEnabled }), [enabled, hasSession, sessionResolved]);

	return <AssistantEnabledContext.Provider value={value}>{children}</AssistantEnabledContext.Provider>;
};
